import { FormControl, FormField, FormItem } from '@/components/ui/form';
import {
  getPnlRoePct,
  getPositionInitMargin,
} from '@/features/account/utils/math';
import useLeverage from '@/hooks/use-leverage';
import useSymbol from '@/hooks/use-symbol';
import { useAccountStore } from '@/store/use-account-store';
import {
  COLLATERAL_DECIMALS,
  COLLATERAL_SYMBOL,
  useMarketStore,
} from '@/store/use-markets-store';
import { OrderType } from '@/types/enums';
import { getIsStatusEval } from '@/features/vest-capital/utils/status';
import { cn } from '@/utils/cn';
import {
  BigDecimal,
  bigIntToDecimalStr,
  formatBigInt,
  formatNumber,
  parseDecimalToBigInt,
  validatedDecimalStr,
} from '@/utils/value-format';
import { ChangeEvent, useEffect, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

import {
  validateFinalLimitPrice,
  validateLimitPrice,
} from '../../utils/helpers';
import { getBboPrice } from '../../utils/math';
import { Input } from '../input';
import { OrderFormSchema } from '../order-form';
import { UnitSelect } from '../unit-select';

type TpslInputProps = {
  orderType: OrderType.STOP_LOSS | OrderType.TAKE_PROFIT;
  shouldReset?: boolean;
};

const pnlUnits = ['%', COLLATERAL_SYMBOL] as const;

type PnlUnit = (typeof pnlUnits)[number];

export default function TpslInput({
  orderType,
  shouldReset = false,
}: TpslInputProps) {
  const symbol = useSymbol();
  const { t } = useTranslation();
  const form = useFormContext<OrderFormSchema>();

  const isBuy = form.watch('order.isBuy');
  const isEval = useAccountStore((state) =>
    getIsStatusEval(state.getCapitalStatus(state.address)),
  );

  const { priceDecimals, sizeDecimals, midPrice, bestPrice } = useStore(
    useMarketStore(symbol),
    useShallow((state) => {
      const { priceDecimals, sizeDecimals } = state.marketSpec;
      const { midPrice } = state.marketData;

      return {
        priceDecimals,
        sizeDecimals,
        midPrice: midPrice,
        bestPrice: isBuy
          ? state.book.bids.at(0)?.at(0)
          : state.book.asks.at(0)?.at(0),
      };
    }),
  );
  const entryBbo = useStore(useMarketStore(symbol), () =>
    getBboPrice(symbol, isBuy),
  );

  const leverage = useLeverage(symbol);

  const name = orderType === OrderType.TAKE_PROFIT ? 'tp' : 'sl';
  const limitPrice = form.watch(`${name}.limitPrice`);
  const size = parseDecimalToBigInt(form.watch('order.size'), sizeDecimals);
  const parentOrderType = form.watch('order.orderType');
  const marketEntryPrice = isEval ? entryBbo : midPrice;
  const marginPrice =
    parentOrderType === OrderType.LIMIT
      ? parseDecimalToBigInt(form.watch('order.limitPrice'), priceDecimals)
      : marketEntryPrice;
  const price =
    parentOrderType === OrderType.LIMIT
      ? marginPrice
      : isEval
        ? marketEntryPrice
        : orderType === OrderType.TAKE_PROFIT
          ? midPrice
          : bestPrice ?? midPrice;

  const { field: limitPriceField } = useController({
    name: `${name}.limitPrice`,
    control: form.control,
  });

  const handleChangePriceInput = (value: string) => {
    value = validatedDecimalStr(value, Number(priceDecimals), 10);
    value = validateLimitPrice(symbol, value);
    const valueBigInt = parseDecimalToBigInt(value, priceDecimals);
    if (marginPrice === null || valueBigInt > marginPrice * 10n) return;
    limitPriceField.onChange(value);
    setLastChanged('price');
  };

  const [pnlUnit, setPnlUnit] = useState<PnlUnit>(pnlUnits[0]);

  const handleChangePnlUnit = (value: PnlUnit) => {
    setPnlUnit(value);
    setLastChanged('pnl');
  };

  useEffect(() => {
    if (!shouldReset) return;
    setLastChanged('price');
    limitPriceField.onChange('');
  }, [shouldReset]);

  const [pnlInput, setPnlInput] = useState('');
  const [pnl, setPnl] = useState<BigDecimal>();
  const [roePct, setRoePct] = useState<BigDecimal>();

  const handleChangePnlInput = (value: string) => {
    const inputValue = value.replace(/,/g, '');
    if (inputValue !== '' && !/^(\d+|\d*\.\d{0,2})$/.test(inputValue)) {
      return;
    }
    setPnlInput((prev) =>
      inputValue.endsWith('.') && prev.length > inputValue.length
        ? inputValue.slice(0, -1)
        : inputValue,
    );
    setLastChanged('pnl');
  };

  const handleBlurPnlInput = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.value.endsWith('.')) return;
    setPnlInput(e.target.value.slice(0, -1));
  };

  const [lastChanged, setLastChanged] = useState<'price' | 'pnl' | null>(null);

  // Set pnl from price
  useEffect(() => {
    if (lastChanged !== 'price') return;

    if (price === null) {
      setPnl(undefined);
      setRoePct(undefined);
      setPnlInput('');
      return;
    }

    // if we want price to update when we change fields, just add marketData to dependency array and setLastChanged to null at end
    if (limitPrice === '') {
      setPnlInput('');
    } else {
      const sign = orderType === OrderType.TAKE_PROFIT ? 1n : -1n;

      const { marketSpec } = useMarketStore(symbol).getState();

      const orderPrice = parseDecimalToBigInt(
        limitPrice,
        marketSpec.priceDecimals,
      );
      const expectedPnl =
        sign * (orderPrice - price) * size * (isBuy ? 1n : -1n);
      const expectedPnlDecimal = formatBigInt(
        expectedPnl,
        COLLATERAL_DECIMALS,
        {
          digits: 2, // quantize to 2 decimal places?
          round: 'ceil',
        },
      );
      const pctChange =
        (BigInt(leverage) *
          (isBuy ? 1n : -1n) *
          sign *
          ((orderPrice - price) * 10n ** 4n)) /
        (price || 1n);

      const pctChangeDecimal = formatBigInt(pctChange, 2n, {
        digits: 0,
        round: 'ceil',
        commas: false,
      });

      setPnl({ bigint: sign * expectedPnl, decimal: expectedPnlDecimal });
      setRoePct({
        bigint: sign * pctChange,
        decimal: pctChangeDecimal,
      });

      switch (pnlUnit) {
        case COLLATERAL_SYMBOL:
          setPnlInput(expectedPnlDecimal);
          break;
        case '%':
          setPnlInput(pctChangeDecimal);
          break;
      }
    }
  }, [leverage, symbol, lastChanged, limitPrice, size, isBuy, price]);

  // Set price from pnl
  useEffect(() => {
    if (lastChanged !== 'pnl') return;

    if (price === null) {
      setPnl(undefined);
      setRoePct(undefined);
      return;
    }

    if (pnlInput === '') {
      limitPriceField.onChange('');
      return;
    }

    if (pnlInput) {
      const sign = orderType === OrderType.TAKE_PROFIT ? 1n : -1n;

      let orderPrice = 0n;

      switch (pnlUnit) {
        case COLLATERAL_SYMBOL: // must have size defined
          if (size === 0n) {
            setPnl(undefined);
            setRoePct(undefined);
          } else {
            const pnl =
              sign * parseDecimalToBigInt(pnlInput, COLLATERAL_DECIMALS);

            const initMargin = getPositionInitMargin(
              symbol,
              size * price,
              10n ** 4n / BigInt(leverage),
            ).bigint;

            orderPrice = price + ((isBuy ? 1n : -1n) * pnl) / size;

            // sign * (orderPrice - price) * size * (parentOrder.isBuy ? 1n : -1n);

            setPnl({
              bigint: pnl,
              decimal: formatNumber(pnlInput, { maxDigits: 2 }),
            });
            setRoePct(getPnlRoePct(pnl, initMargin));
          }
          break;
        case '%':
          const pnlPct =
            ((isBuy ? 1n : -1n) * sign * parseDecimalToBigInt(pnlInput, 4n)) /
            BigInt(leverage);

          orderPrice = (price * (10n ** 6n + pnlPct)) / 10n ** 6n;

          const pnl = (isBuy ? 1n : -1n) * sign * (orderPrice - price) * size;

          setPnl({
            bigint: sign * pnl,
            decimal: formatBigInt(pnl, COLLATERAL_DECIMALS, {
              digits: 2,
              round: 'ceil',
            }),
          });
          setRoePct({
            bigint: sign * pnlPct,
            decimal: formatNumber(pnlInput, {
              digits: 0,
              round: 'ceil',
              commas: false,
            }),
          });

          break;
      }

      if (orderPrice <= 0n) {
        limitPriceField.onChange('');
      } else {
        const priceInput = bigIntToDecimalStr(orderPrice, priceDecimals);
        limitPriceField.onChange(validateFinalLimitPrice(symbol, priceInput));
      }
    }
  }, [
    pnlInput,
    pnlUnit,
    leverage,
    lastChanged,
    size,
    priceDecimals,
    price,
    isBuy,
  ]);

  return (
    <div className="flex min-h-12 flex-col gap-1">
      <div className="flex items-center gap-3">
        <FormField
          control={form.control}
          name={`${name}.limitPrice`}
          render={({ field }) => (
            <FormItem className="flex-1">
              <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2.5">
                <span className="text-sm text-vestgrey-100">
                  {orderType === OrderType.TAKE_PROFIT
                    ? t('tpslInput.tp')
                    : t('tpslInput.sl')}{' '}
                  {t('tpslInput.price')}
                </span>
                <div className="flex flex-1">
                  <FormControl>
                    <Input
                      {...field}
                      className="flex-1"
                      onChange={handleChangePriceInput}
                    />
                  </FormControl>
                </div>
              </div>
            </FormItem>
          )}
        />
        <div className="flex flex-1 flex-col gap-1 sm:flex-row sm:items-center sm:gap-2.5">
          <span className="w-8 text-right text-sm text-vestgrey-100">
            {orderType === OrderType.TAKE_PROFIT
              ? t('tpslInput.profit')
              : t('tpslInput.loss')}
          </span>
          <div className="flex flex-1">
            <Input
              className="flex-1"
              value={pnlInput}
              onChange={handleChangePnlInput}
              onBlur={handleBlurPnlInput}
              disabled={price === null}
            />
            <UnitSelect
              units={pnlUnits}
              onSelect={(value) => handleChangePnlUnit(value as PnlUnit)}
              disabled={size === 0n || price === null}
            />
          </div>
        </div>
      </div>

      {size > 0n && limitPrice && pnlInput && pnl && roePct && (
        <div className="text-right text-sm">
          <span className="text-vestgrey-100">{t('tpslInput.estPnl')} </span>

          <span
            className={cn(
              'h-12 text-foreground opacity-100',
              pnl.bigint < 0n && 'text-red',
              pnl.bigint > 0n && 'text-green',
            )}
          >
            {pnl.bigint >= 0n ? '+' : '-'}
            {pnl.decimal.replace('-', '')} / {roePct.decimal.replace('-', '')}%
          </span>
        </div>
      )}
    </div>
  );
}
